import type { PropagationStateDto } from "@eu.mdotm.api/sphere";

/**
 * Propagation of instrument customizations to the Portfolio Studio entities that depend on them.
 *
 * The `PATCH` endpoints no longer trigger the propagation webhook directly: they mark the customer's
 * data dirty and the propagation is debounced server-side. This module owns the client-side view of
 * the contract (`MDOT-24644`) and the one pure transition the page applies to it.
 *
 * Every field of the payload exists for a reason:
 * - `dirty` — `status` alone cannot express "an edit was made while a job was already running".
 * - `lastRun` — the only way to render a past failure after a reload.
 * - `revision` — see {@link applyPropagationState}.
 *
 * `scheduledAt` is an absolute, server-generated ISO timestamp, rendered as-is. It is never derived
 * client-side from a duration: `now + delta` is wrong under clock skew and after the tab sleeps.
 */
export type PropagationState = PropagationStateDto;

/**
 * The revision guard. A single counter per customer, incremented on every propagation-state
 * transition, carried by every payload — the write responses, the state `GET`, the flush response and
 * all three SSE events, which is why this is the *only* applier the page needs.
 *
 * An incoming state is applied only when its revision is `>=` the current one. `>=` rather than `>` is
 * load-bearing: a flush and the job start it triggers are ONE transition, so the flush response and its
 * `started` event share a revision and must not cancel each other out depending on which one the
 * browser happens to see first.
 *
 * The reverse case is covered by the same rule: when a run completes and immediately re-arms the
 * debounce, the backend stamps the `done` event with the revision *below* the re-armed document's, so
 * whichever arrives second cannot walk the schedule back to nothing.
 */
export function applyPropagationState(current: PropagationState | null, incoming: PropagationState): PropagationState {
	if (current !== null && incoming.revision < current.revision) {
		return current;
	}
	return incoming;
}
